import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs, OrchestratorInvokeMessagePayload } from "@expert/shared-types";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { type TimelineFeatures, useFeatures } from "@expert/features";
import { fallbackGaiaPayloadProps, getFallbackPartner, useGaiaWebsocket } from "@expert/gaia";

import { useTimelineStore } from "../state";

export function useRequestAutoCompleteSuggestions(logger: Logger, gaiaPayloadProps: GaiaPayloadConfigs) {
    const websocketObj = useGaiaWebsocket();
    const { dispatcher } = useReactAnalytics();
    const {
        features: { isChatExpertAssistAutoCompleteEnabled },
    } = useFeatures<TimelineFeatures>();
    const { expertId, currentSessionId: sessionId, getTimelineState } = useTimelineStore();
    const { callSid, chatId, partner } = getTimelineState(sessionId);

    const isChatSessionWithAutoComplete = !!chatId && isChatExpertAssistAutoCompleteEnabled;

    if ((!callSid && !isChatSessionWithAutoComplete) || !sessionId) {
        return {};
    }

    const { autoCompleteAppName } = gaiaPayloadProps ?? fallbackGaiaPayloadProps(chatId ? "chat" : "voice");
    const requestAutoCompleteSuggestions = (textInput: string, isProactiveSuggestion = false) => {
        const fetchAutoCompleteSuggestionsPayload: OrchestratorInvokeMessagePayload = {
            action: "orchestrator-invoke",
            appName:
                autoCompleteAppName ?? (chatId ? "expert-assist-messaging-autocomplete" : "expert-assist-autocomplete"),
            correlationId: crypto.randomUUID(),
            message: textInput,
            messageId: crypto.randomUUID(),
            messageType: "AutoComplete",
            partner: getFallbackPartner({ partner }),
            sessionId: callSid || chatId || sessionId,
            sessionGroupId: sessionId,
            userId: expertId,
            userType: "expert",
        };
        if (!isProactiveSuggestion) {
            logger
                .child({ module: "useRequestAutoCompleteSuggestions" })
                // The length, not the text: the expert's typed message can hold customer details.
                .info({ inputLength: textInput.length }, "AutoComplete | suggestion requested");
        }
        void dispatcher.dispatchBusinessEvent("AutoComplete_SuggestionRequested", {
            input: textInput,
            isProactiveSuggestion,
        });

        websocketObj?.sendJsonMessage(fetchAutoCompleteSuggestionsPayload);
    };

    return { requestAutoCompleteSuggestions };
}
